/**
 * A receipt, and a VAT invoice, as paper.
 *
 * Shared because two apps print a receipt: the till at settlement, and the
 * back office when somebody asks "can you reprint that?" days later. It is the
 * fiscal document, so it should not be drawn two ways. The invoice is drawn
 * here too, from the same reader and the same itemised bill: an invoice for a
 * meal shows the same meal, and one renderer is one set of rules about what a
 * frozen document says.
 *
 * Rendered from the frozen payload alone, never from the live order or menu,
 * so a document printed tomorrow — or reprinted in eighteen months — shows
 * what the guest paid today. The payload is an open object on the wire and is
 * read field by field: a document frozen months ago may predate a field, and
 * one that renders without it beats one that throws.
 *
 * A reprint is marked COPY. The server returns the same document with the same
 * number and counts the reprint; a second piece of paper that could pass for an
 * original is what a fiscal sequence exists to prevent.
 *
 * Money and tender names are passed in, because this package does not depend
 * on the API client and should not start: it cannot know the currency rules or
 * the product's words for a tender.
 */

import { Fragment } from 'react';

import './receipt.css';

/** Something that came off a dish or off the whole bill. */
interface PayloadDiscount {
  /**
   * `deal` is a meal deal and `promotion` a promotion, both applied by rule;
   * `coupon` is a code the guest handed over; `discount` was taken off by hand.
   */
  kind: 'deal' | 'promotion' | 'coupon' | 'discount';
  /** The reason's words when it was given, e.g. "Regular guest". */
  label: string | null;
  amount: string;
}

interface PayloadLine {
  name: string;
  variant: string | null;
  qty: string;
  /** Absent on documents frozen before it was carried. */
  unit_price?: string;
  line_total: string;
  modifiers: { name: string; price: string }[];
  /** What came off this dish. Absent on documents frozen before it. */
  discounts?: PayloadDiscount[];
}

interface PayloadPayment {
  tender_type: string;
  amount: string;
  /**
   * What changed hands. Above `amount` only when a note covered more than the
   * bill, or a voucher was worth more than the meal. Absent on receipts frozen
   * before payments carried it.
   */
  tendered?: string;
  /** What came back with this note. Absent on receipts frozen before it. */
  change?: string;
  tip: string;
}

interface PayloadServiceCharge {
  name: string;
  percent: string;
  amount: string;
}

interface Party {
  name: string | null;
  taxNumber: string | null;
  address: string | null;
}

interface TaxBand {
  tax_class: string;
  label: string;
  percent: string;
  net: string;
  tax: string;
  gross: string;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const str = (value: unknown) => (typeof value === 'string' ? value : null);

function discounts(value: unknown): PayloadDiscount[] {
  return Array.isArray(value)
    ? (value as PayloadDiscount[]).filter((d) => typeof d === 'object' && d !== null)
    : [];
}

/** Everything both documents read off a frozen payload. */
function read(payload: Record<string, unknown>) {
  const location =
    typeof payload.location === 'object' && payload.location !== null
      ? (payload.location as Record<string, unknown>)
      : {};
  const table = str(payload.table);
  return {
    locationName: str(location.name),
    orderNumber: typeof payload.order_number === 'number' ? payload.order_number : null,
    // Receipts issued before the payload carried the table's label froze its
    // id instead. An identifier printed as though it were a table number is
    // worse than no table at all, so an id-shaped value is left off.
    table: table !== null && !UUID.test(table) ? table : null,
    guests: typeof payload.guests === 'number' ? payload.guests : null,
    takeaway: payload.takeaway === true,
    lines: Array.isArray(payload.lines) ? (payload.lines as PayloadLine[]) : [],
    subtotal: str(payload.subtotal),
    offBill: discounts(payload.discounts),
    // Absent on receipts frozen before service charges existed, null on a bill
    // that had none. Either way there is no line to print.
    serviceCharge:
      typeof payload.service_charge === 'object' && payload.service_charge !== null
        ? (payload.service_charge as PayloadServiceCharge)
        : null,
    tax: str(payload.tax_total),
    total: str(payload.total),
    payments: Array.isArray(payload.payments) ? (payload.payments as PayloadPayment[]) : [],
    tips: str(payload.tips),
    change: str(payload.change),
    closedAt: str(payload.closed_at),
  };
}

/**
 * A party off a frozen invoice: the seller's name is `legal_name` and the
 * buyer's is `name`, both read here so the two blocks are drawn by one reader.
 */
function readParty(value: unknown): Party | null {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Record<string, unknown>;
  const text = (raw: unknown) => (typeof raw === 'string' && raw !== '' ? raw : null);
  return {
    name: text(record.legal_name) ?? text(record.name),
    taxNumber: text(record.tax_number),
    address: text(record.address),
  };
}

/** The bands and totals off a frozen invoice, or null if it carries none. */
function readTax(value: unknown): {
  bands: TaxBand[];
  net: string;
  tax: string;
  gross: string;
  ratesOn: string | null;
  notTaxed: string | null;
} | null {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Record<string, unknown>;
  if (!Array.isArray(record.bands)) return null;
  const figure = (raw: unknown) => (typeof raw === 'string' ? raw : '0.00');
  return {
    bands: record.bands as TaxBand[],
    net: figure(record.net),
    tax: figure(record.tax),
    gross: figure(record.gross),
    ratesOn: str(record.rates_on),
    // Absent on invoices issued before service charges were counted apart.
    notTaxed: str(record.not_taxed),
  };
}

/** What a discount is called on paper. */
function offLabel(off: PayloadDiscount): string {
  return off.kind === 'deal' ? 'Meal deal' : (off.label ?? 'Discount');
}

/**
 * The bill, dish by dish: what was ordered, how many at what price, what went
 * with it, and what came off it and why.
 *
 * The one place a frozen bill is itemised, for both documents. The unit price
 * shows when there is more than one, because "3 × Roast 36.00" asks the reader
 * to divide; documents frozen before the unit price or the discounts were
 * carried show what they carry.
 */
export function BillLines({
  payload,
  money,
}: {
  payload: Record<string, unknown>;
  money: (amount: string) => string;
}) {
  const doc = read(payload);
  return (
    <ul className="rcpt__lines">
      {doc.lines.map((line, index) => {
        const qty = Number(line.qty);
        return (
          <li key={index}>
            <span className="rcpt__line">
              <span>
                {qty !== 1 && `${qty} × `}
                {line.name}
                {line.variant && ` (${line.variant})`}
              </span>
              <span className="tabular">{money(line.line_total)}</span>
            </span>
            {qty !== 1 && line.unit_price !== undefined && (
              <span className="rcpt__mod tabular">{money(line.unit_price)} each</span>
            )}
            {line.modifiers.map((modifier, i) => (
              <span key={i} className="rcpt__mod">
                + {modifier.name}
              </span>
            ))}
            {discounts(line.discounts).map((off, i) => (
              <span key={`off-${i}`} className="rcpt__line rcpt__off">
                <span>{offLabel(off)}</span>
                <span className="tabular">−{money(off.amount)}</span>
              </span>
            ))}
          </li>
        );
      })}
    </ul>
  );
}

/** Subtotal, what came off the whole bill, and the total. Shared by both. */
function BillTotals({
  doc,
  money,
  taxLine,
}: {
  doc: ReturnType<typeof read>;
  money: (amount: string) => string;
  /** The "of which tax" figure; the invoice passes its extracted tax. */
  taxLine: string | null;
}) {
  const figure = (value: string | null) => (value === null ? '—' : money(value));
  return (
    <>
      <dt>Subtotal</dt>
      <dd className="tabular">{figure(doc.subtotal)}</dd>
      {doc.offBill.map((off, index) => (
        <Pair key={index} term={offLabel(off)} value={`−${money(off.amount)}`} />
      ))}
      {/* After what came off, because it is worked out on the bill after it. */}
      {doc.serviceCharge && (
        <Pair
          term={`${doc.serviceCharge.name} ${Number(doc.serviceCharge.percent)}%`}
          value={money(doc.serviceCharge.amount)}
        />
      )}
      <dt className="rcpt__total">Total</dt>
      <dd className="rcpt__total tabular">{figure(doc.total)}</dd>
      <dt>Of which tax</dt>
      <dd className="tabular">{figure(taxLine)}</dd>
    </>
  );
}

/** Across a practice slip, on screen and on paper. */
export const PRACTICE_MARK = 'PRACTICE · NOT A RECEIPT';

export interface ReceiptPaperProps {
  reference: string;
  reprintCount: number;
  issuedAt: string;
  payload: Record<string, unknown>;
  /** The product's money formatting. */
  money: (amount: string) => string;
  /** A tender key as people say it: "Card", "Account". */
  tender: (key: string) => string;
  /**
   * The site's name, lines and logo, where the plan has branded receipts.
   * Presentation only: nothing here comes from the frozen payload, so a
   * reprint after a rebrand shows the new logo over the old figures.
   */
  branding?: ReceiptBrandingLook | null | undefined;
  /** How the site writes dates: "fr-FR". The browser's own when omitted. */
  locale?: string | undefined;
}

export interface ReceiptBrandingLook {
  display_name: string;
  header: string | null;
  footer: string | null;
  logo: string | null;
}

export function ReceiptPaper({
  reference,
  reprintCount,
  issuedAt,
  payload,
  money,
  tender,
  branding,
  locale,
}: ReceiptPaperProps) {
  const doc = read(payload);
  const copy = reprintCount > 0;
  const perLineChange = doc.payments.some((payment) => payment.change !== undefined);

  return (
    <article className="rcpt" aria-label="Receipt">
      {/* A practising till's slip (`practice` on the till): laid out like a
          receipt so staff learn on it, and saying it is not one. */}
      {payload.practice === true && <p className="rcpt__copy">{PRACTICE_MARK}</p>}
      {copy && <p className="rcpt__copy">COPY</p>}
      {branding?.logo && <img className="rcpt__logo" src={branding.logo} alt="" />}
      {(branding?.display_name ?? doc.locationName) && (
        <p className="rcpt__site">{branding?.display_name ?? doc.locationName}</p>
      )}
      {branding?.header && <p className="rcpt__brand-line">{branding.header}</p>}
      <p className="rcpt__ref tabular">{reference}</p>
      <Meta doc={doc} />
      <p className="rcpt__meta">
        {new Date(doc.closedAt ?? issuedAt).toLocaleString(locale, {
          dateStyle: 'medium',
          timeStyle: 'short',
        })}
      </p>

      <BillLines payload={payload} money={money} />

      <dl className="rcpt__totals">
        <BillTotals doc={doc} money={money} taxLine={doc.tax} />
        {/* What the guest watched happen. A £30 note against a £25 bill is
            "£30.00 given" with £5 back, and showing only the £25 that came off
            the bill reads as the wrong number to the person who handed over the
            note. Older receipts have no `tendered` and show the one figure they
            froze. */}
        {doc.payments.map((payment, index) => (
          <Fragment key={index}>
            <Pair term={tender(payment.tender_type)} value={money(payment.amount)} />
            {payment.tendered !== undefined &&
              Number(payment.tendered) > Number(payment.amount) && (
                <>
                  <Pair
                    term={`${tender(payment.tender_type)} given`}
                    value={money(payment.tendered)}
                  />
                  {/* Under the note it came from. Two notes on a split give
                      change twice, and one figure at the foot cannot say which
                      of them either amount belonged to. */}
                  {payment.change !== undefined && Number(payment.change) !== 0 && (
                    <Pair term="Change" value={money(payment.change)} />
                  )}
                </>
              )}
          </Fragment>
        ))}
        {doc.tips !== null && Number(doc.tips) !== 0 && (
          <Pair term="Tips" value={money(doc.tips)} />
        )}
        {/* The bill's single change figure, for receipts frozen before payments
            carried their own. Printing it as well as the per-line ones would be
            the same money twice. */}
        {!perLineChange && doc.change !== null && Number(doc.change) !== 0 && (
          <Pair term="Change" value={money(doc.change)} />
        )}
      </dl>

      {branding?.footer && <p className="rcpt__brand-foot">{branding.footer}</p>}
    </article>
  );
}

/** One line of a receipt as a receipt printer takes it. */
export interface PrintLine {
  text: string;
  align?: 'left' | 'center';
  bold?: boolean;
  /** Double height and width: the site's name, the total, COPY. */
  big?: boolean;
}

/** A name on the left and a figure on the right, `width` characters wide. */
export function twoColumns(left: string, right: string, width: number): string {
  const room = width - right.length - 1;
  const name = left.length > room ? `${left.slice(0, Math.max(room - 1, 0))}…` : left;
  return `${name}${' '.repeat(Math.max(width - name.length - right.length, 1))}${right}`;
}

/**
 * The same receipt as `ReceiptPaper`, as lines of fixed-width text for a
 * receipt printer driven directly (Epson ePOS, Star WebPRNT): one reader of
 * the frozen payload, so paper from a browser and paper from a thermal printer
 * say the same thing. `width` is characters per line: 48 on 80mm paper, 32 on
 * 58mm. No logo — a direct printer gets text, and the name says whose it is.
 */
export function receiptLines({
  reference,
  reprintCount,
  issuedAt,
  payload,
  money,
  tender,
  branding,
  locale,
  width = 48,
}: ReceiptPaperProps & { width?: number }): PrintLine[] {
  const doc = read(payload);
  const out: PrintLine[] = [];
  const pair = (left: string, right: string, bold = false) =>
    out.push({ text: twoColumns(left, right, width), bold });
  const rule = () => out.push({ text: '-'.repeat(width) });

  const practice = payload.practice === true;
  if (practice) out.push({ text: PRACTICE_MARK, align: 'center', bold: true });
  if (reprintCount > 0) out.push({ text: 'COPY', align: 'center', big: true, bold: true });
  const site = branding?.display_name ?? doc.locationName;
  if (site) out.push({ text: site, align: 'center', big: true, bold: true });
  if (branding?.header) {
    for (const line of branding.header.split('\n')) out.push({ text: line, align: 'center' });
  }
  out.push({ text: reference, align: 'center' });
  const meta = [
    doc.orderNumber !== null ? `Order ${doc.orderNumber}` : null,
    doc.table ? `Table ${doc.table}` : null,
    doc.guests !== null ? `${doc.guests} guest${doc.guests === 1 ? '' : 's'}` : null,
    doc.takeaway ? 'Take away' : null,
  ].filter(Boolean);
  if (meta.length) out.push({ text: meta.join(' · '), align: 'center' });
  out.push({
    text: new Date(doc.closedAt ?? issuedAt).toLocaleString(locale, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }),
    align: 'center',
  });
  rule();

  for (const line of doc.lines) {
    const qty = Number(line.qty);
    const name = `${qty !== 1 ? `${qty} x ` : ''}${line.name}${line.variant ? ` (${line.variant})` : ''}`;
    pair(name, money(line.line_total));
    if (qty !== 1 && line.unit_price !== undefined) {
      out.push({ text: `   ${money(line.unit_price)} each` });
    }
    for (const modifier of line.modifiers) out.push({ text: `   + ${modifier.name}` });
    for (const off of discounts(line.discounts))
      pair(`   ${offLabel(off)}`, `-${money(off.amount)}`);
  }
  rule();

  const figure = (value: string | null) => (value === null ? '-' : money(value));
  pair('Subtotal', figure(doc.subtotal));
  for (const off of doc.offBill) pair(offLabel(off), `-${money(off.amount)}`);
  if (doc.serviceCharge) {
    pair(
      `${doc.serviceCharge.name} ${Number(doc.serviceCharge.percent)}%`,
      money(doc.serviceCharge.amount),
    );
  }
  out.push({
    text: twoColumns('TOTAL', figure(doc.total), Math.floor(width / 2)),
    bold: true,
    big: true,
  });
  pair('Of which tax', figure(doc.tax));
  rule();

  const perLineChange = doc.payments.some((payment) => payment.change !== undefined);
  for (const payment of doc.payments) {
    pair(tender(payment.tender_type), money(payment.amount));
    if (payment.tendered !== undefined && Number(payment.tendered) > Number(payment.amount)) {
      pair(`${tender(payment.tender_type)} given`, money(payment.tendered));
      if (payment.change !== undefined && Number(payment.change) !== 0) {
        pair('Change', money(payment.change));
      }
    }
  }
  if (doc.tips !== null && Number(doc.tips) !== 0) pair('Tips', money(doc.tips));
  if (!perLineChange && doc.change !== null && Number(doc.change) !== 0) {
    pair('Change', money(doc.change));
  }
  if (branding?.footer) {
    out.push({ text: '' });
    for (const line of branding.footer.split('\n')) out.push({ text: line, align: 'center' });
  }
  return out;
}

export interface InvoicePaperProps {
  reference: string;
  reprintCount: number;
  issuedAt: string;
  payload: Record<string, unknown>;
  /** The product's money formatting. */
  money: (amount: string) => string;
  /** How the site writes dates: "fr-FR". The browser's own when omitted. */
  locale?: string | undefined;
}

/**
 * A VAT invoice, as the document it is: who charged whom, the bill itemised,
 * and the total split by rate.
 *
 * The itemised bill is the receipt's, drawn by the same `BillLines`, so the
 * two documents for one meal cannot disagree about what was eaten. Its "of
 * which tax" is the tax the invoice extracted rather than the receipt's zero —
 * the server writes the same figure into both fields of an invoice so that no
 * renderer can show two answers to the one question the document exists for.
 *
 * `net + tax = gross` holds on every band and on the total row, which is the
 * property that makes an invoice add up, and the bands add up to the bill's
 * total because they are worked out on what was paid, after every discount.
 */
export function InvoicePaper({
  reference,
  reprintCount,
  issuedAt,
  payload,
  money,
  locale,
}: InvoicePaperProps) {
  const doc = read(payload);
  const seller = readParty(payload.seller);
  const buyer = readParty(payload.buyer);
  const tax = readTax(payload.tax);
  const date = (iso: string, withTime: boolean) =>
    new Date(iso).toLocaleString(
      locale,
      withTime ? { dateStyle: 'medium', timeStyle: 'short' } : { dateStyle: 'medium' },
    );

  return (
    <article className="inv" aria-label="VAT invoice">
      {reprintCount > 0 && <p className="rcpt__copy">COPY</p>}
      <header className="inv__head">
        <p className="inv__title">Invoice</p>
        <p className="inv__ref tabular">{reference}</p>
        <p className="rcpt__meta">Issued {date(issuedAt, true)}</p>
        <Meta doc={doc} />
      </header>

      <div className="inv__parties">
        <PartyBlock title="From" party={seller} blankTaxNumber="—" />
        {/* An absent registration is ordinary rather than missing: a sole
            trader or a private individual has none to quote. */}
        <PartyBlock title="To" party={buyer} blankTaxNumber="No tax registration given" />
      </div>

      <BillLines payload={payload} money={money} />

      <dl className="rcpt__totals">
        <BillTotals doc={doc} money={money} taxLine={tax?.tax ?? doc.tax} />
      </dl>

      {tax === null ? (
        <p className="inv__note">
          This document was frozen without a tax breakdown, so there is nothing to show by rate.
          What it says is what it said when it was issued.
        </p>
      ) : (
        <table className="inv__bands">
          <thead>
            <tr>
              <th>Rate</th>
              <th className="inv__num">Net</th>
              <th className="inv__num">Tax</th>
              <th className="inv__num">Gross</th>
            </tr>
          </thead>
          <tbody>
            {tax.bands.map((band) => (
              <tr key={band.tax_class}>
                <td>{band.label}</td>
                <td className="inv__num tabular">{money(band.net)}</td>
                <td className="inv__num tabular">{money(band.tax)}</td>
                <td className="inv__num tabular">{money(band.gross)}</td>
              </tr>
            ))}
            {/* A service charge no rate applies to: paid, and outside every
                band. Without the row the bands add up to less than the bill. */}
            {tax.notTaxed !== null && Number(tax.notTaxed) !== 0 && (
              <tr>
                <td>Service charge, not taxed</td>
                <td className="inv__num tabular">{money(tax.notTaxed)}</td>
                <td className="inv__num tabular">{money('0.00')}</td>
                <td className="inv__num tabular">{money(tax.notTaxed)}</td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr>
              <td>Total</td>
              <td className="inv__num tabular">{money(tax.net)}</td>
              <td className="inv__num tabular">{money(tax.tax)}</td>
              <td className="inv__num tabular">{money(tax.gross)}</td>
            </tr>
          </tfoot>
        </table>
      )}

      <p className="inv__note">
        Prices include tax; the rates above take it out of what was paid rather than adding it
        on.
        {tax?.ratesOn &&
          ` Rates as they stood on ${date(tax.ratesOn, false)}, the day of the sale.`}
      </p>
    </article>
  );
}

/** Order, table, guests and eat-in or take-away, on one line. */
function Meta({ doc }: { doc: ReturnType<typeof read> }) {
  return (
    <p className="rcpt__meta">
      {doc.orderNumber !== null && `Order ${doc.orderNumber}`}
      {doc.table && ` · Table ${doc.table}`}
      {doc.guests !== null && ` · ${doc.guests} guest${doc.guests === 1 ? '' : 's'}`}
      {/* Said because it is why a dish can sit in a lower band than at a table. */}
      {doc.takeaway && ' · Take away'}
    </p>
  );
}

function PartyBlock({
  title,
  party,
  blankTaxNumber,
}: {
  title: string;
  party: Party | null;
  blankTaxNumber: string;
}) {
  return (
    <div className="inv__party">
      <p className="inv__party-title">{title}</p>
      <p className="inv__party-name">{party?.name ?? 'Not recorded'}</p>
      <p className="tabular">{party?.taxNumber ?? blankTaxNumber}</p>
      {party?.address && <p className="inv__address">{party.address}</p>}
    </div>
  );
}

function Pair({ term, value }: { term: string; value: string }) {
  return (
    <>
      <dt>{term}</dt>
      <dd className="tabular">{value}</dd>
    </>
  );
}
